// Pure helpers for the map screen — the bits worth testing in isolation
// from the (heavy, router/Apollo/Google-Maps-bound) map screen component.
// Discrete jobs:
//
//   - pickInitialView: precedence rules for the first render's region
//     (focus pair → deep-link URL params → persisted last view → null =
//     geolocation).
//   - viewToRegion: serialized view {lat,lng,z} → the Region shape the
//     map view consumes (with the 1.5× aspect ratio we've used historically).
//   - regionToView: Region from a pan/zoom callback → the rounded, stable
//     view we write to both the URL and localStorage.

import {
  latitudeDeltaToZoom,
  zoomToLatitudeDelta,
} from "#app/components/map/map-styles";
import type { Region } from "#app/components/map/map-view";
import type { Omittable } from "@salestrak/ui";

// A fully-resolved map view. All three fields are required to construct a
// Region — the persisted/URL forms below use `null`/`undefined` for "not set".
export type MapView = { lat: number; lng: number; z: number };

// Shape persisted to localStorage. Nullable members so an unset slot reads as
// `null` rather than being absent (matches usePersistedState's defaults shape).
export type PersistedMapView = {
  lat: number | null;
  lng: number | null;
  z: number | null;
};

// Zoom the map lands on when focusing a single address (a global-search pick).
// Shared with the map screen's post-mount navigateToLocation so a cold deep
// link and an in-place focus settle at the same zoom.
export const FOCUS_ZOOM = 16;

// The map view's URL query params — every value is a string|undefined, read
// straight off the route's search params (absent from the URL as an explicit
// `undefined`, so the members relax). focusLat/focusLng are the one-shot
// "focus this address" pair; lat/lng/z are the ordinary camera position.
export type UrlMapViewParams = Omittable<{
  lat?: string;
  lng?: string;
  z?: string;
  focusLat?: string;
  focusLng?: string;
}>;

// The one-shot "focus this address" pair, or null when it is absent or
// unusable. Gate the Number() coercion on a non-empty trimmed string: a param
// present but empty (`?focusLat=&focusLng=`) reaches us as `""` and
// `Number("")` returns 0, so a blank pair would otherwise read as the perfectly
// finite coordinate (0, 0) — Null Island. The map screen's focus effect shares
// this so the URL cannot mean one thing to the initial view and another to the
// post-mount pan (it used to coerce inline and pan to 0,0 on a blank pair).
export function parseFocusCoords(
  url: UrlMapViewParams,
): { lat: number; lng: number } | null {
  const lat = url.focusLat?.trim() ? Number(url.focusLat) : NaN;
  const lng = url.focusLng?.trim() ? Number(url.focusLng) : NaN;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

// Pick the initial view to seed the map with. Precedence:
//   1. ?focusLat&focusLng — an explicit "put me at this address" navigation
//   2. ?lat&lng&z         — an ordinary deep-linked camera position
//   3. the persisted last view
//   4. null — caller leaves initialRegion undefined; the map falls through to
//      its geolocation default
//
// Focus wins because it is a destination the user just chose, whereas lat/lng/z
// is only where the camera happened to be. Without it, arriving at /map from
// global search mounted the map at the persisted last view and relied on a
// post-mount pan to correct it — which is why selecting an address worked when
// already on the map (no mount, pins already loaded) but not from another
// screen. Focus carries no zoom of its own, so it pairs with FOCUS_ZOOM.
export function pickInitialView(
  url: UrlMapViewParams,
  persisted: PersistedMapView,
): MapView | null {
  const focus = parseFocusCoords(url);
  if (focus) return { ...focus, z: FOCUS_ZOOM };
  // Gate the Number() coercion on a non-empty trimmed string. A param present
  // but empty (`?lat=&lng=&z=`) reaches us as `""`, and `Number("")` returns
  // 0 — without this guard it would silently resolve to {0,0,0} (Null Island,
  // zoom 0) and bypass the persisted-view fallback.
  const urlLat = url.lat?.trim() ? Number(url.lat) : NaN;
  const urlLng = url.lng?.trim() ? Number(url.lng) : NaN;
  const urlZ = url.z?.trim() ? Number(url.z) : NaN;
  if (
    Number.isFinite(urlLat) &&
    Number.isFinite(urlLng) &&
    Number.isFinite(urlZ)
  ) {
    return { lat: urlLat, lng: urlLng, z: urlZ };
  }
  if (persisted.lat !== null && persisted.lng !== null && persisted.z !== null) {
    return { lat: persisted.lat, lng: persisted.lng, z: persisted.z };
  }
  return null;
}

// Build the Region the map view consumes from a discrete view. The ×1.5
// longitudeDelta matches the aspect ratio we've used historically for the
// initial render — the map view itself adjusts the deltas to the actual
// viewport as soon as it lays out.
export function viewToRegion(view: MapView): Region {
  const latitudeDelta = zoomToLatitudeDelta(view.z);
  return {
    latitude: view.lat,
    longitude: view.lng,
    latitudeDelta,
    longitudeDelta: latitudeDelta * 1.5,
    zoom: view.z,
  };
}

// Reduce a Region fired by the map view during pan/zoom to the stable shape
// we serialize to the URL and to localStorage: lat/lng at 5-decimal precision
// (~1m), zoom rounded to int. Prefers r.zoom when the underlying map reports
// it directly; otherwise derives it from latitudeDelta.
export function regionToView(r: Region): MapView {
  const zRaw = r.zoom ?? latitudeDeltaToZoom(r.latitudeDelta);
  return {
    lat: Number(r.latitude.toFixed(5)),
    lng: Number(r.longitude.toFixed(5)),
    z: Math.round(zRaw),
  };
}
